import { RevealItem, ScrollReveal } from '#/components/common/ScrollReveal'
import { TextAnimate } from '#/components/common/TextAnimate'
import { Button } from '#/components/ui/button'
import headerIcon from '#/assets/header-icon.svg'
import aboutDecor from '#/assets/about-decor.svg'
import { LuSend } from 'react-icons/lu'
import courseAboutDefault from '#/assets/courses/course-1.png'
import type { Course } from '#/features/courses/types/course.types'
import { Link } from '@tanstack/react-router'
import { getImageUrl } from '#/lib/imageUrl'

interface CourseAboutSectionProps {
  course?: Course
}

export default function CourseAboutSection({
  course,
}: CourseAboutSectionProps) {
  const aboutImage = getImageUrl(course?.image, courseAboutDefault)
  const courseTitle = course?.title || 'Course Details'
  const description = course?.curriculumDescription || ''
  const highlights = course?.highlights || []

  // If no description or highlights, hide section or render minimal
  if (!description && highlights.length === 0 && !course?.image) {
    return null
  }

  return (
    <section
      className="relative w-full py-20 overflow-hidden"
      id="about-course"
    >
      <img
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute left-0 top-0 lg:w-2/5 w-3/4 opacity-40 max-lg:origin-top-left"
        src={aboutDecor}
      />

      <div className="container mx-auto flex flex-col items-center gap-14">
        <ScrollReveal className="flex flex-col items-center gap-3">
          <RevealItem>
            <img alt="" src={headerIcon} />
          </RevealItem>
          <RevealItem>
            <h3 className="text-xl font-bold text-primary-900 text-center">
              ABOUT THIS COURSE
            </h3>
          </RevealItem>
          <RevealItem className="w-full text-center">
            <h2 className="text-5xl font-bold text-neutral-900 max-md:text-4xl max-sm:text-3xl">
              {courseTitle}
            </h2>
          </RevealItem>
        </ScrollReveal>

        <div className="grid w-full grid-cols-1 gap-16 lg:grid-cols-2 items-center">
          {/* Left — Image from API */}
          <ScrollReveal stagger={0.1}>
            <RevealItem direction="left">
              <div className="relative mx-auto w-full max-w-[80%] lg:max-w-[520px]">
                <div
                  className="pointer-events-none absolute -right-[14] -top-14 size-48 opacity-50"
                  style={{
                    backgroundImage:
                      'radial-gradient(circle, var(--primary-500) 2.5px, transparent 2.5px)',
                    backgroundSize: '15px 15px',
                  }}
                />
                <div
                  className="pointer-events-none absolute -bottom-6 -left-6 size-28 opacity-30"
                  style={{
                    backgroundImage:
                      'radial-gradient(circle, var(--secondary-500) 1.5px, transparent 1.5px)',
                    backgroundSize: '12px 12px',
                  }}
                />
                <div className="absolute left-5 top-5 h-full w-full rounded-2xl border-2 border-primary-400/40" />

                <div className="relative overflow-hidden rounded-2xl">
                  <img
                    src={aboutImage}
                    alt={courseTitle}
                    className="w-full object-cover aspect-[4/3]"
                  />
                  <div className="absolute inset-0 bg-linear-to-tr from-primary-900/30 via-transparent to-transparent" />
                </div>
              </div>
            </RevealItem>
          </ScrollReveal>

          {/* Right — Description + highlights + CTA */}
          <ScrollReveal className="flex flex-col gap-8" stagger={0.14}>
            {description && (
              <RevealItem>
                <div className="flex flex-col gap-4">
                  <TextAnimate
                    by="word"
                    animation="fadeIn"
                    duration={1.1}
                    className="text-lg leading-relaxed text-neutral-600"
                  >
                    {description}
                  </TextAnimate>
                </div>
              </RevealItem>
            )}

            {/* Highlights from API */}
            {highlights.length > 0 && (
              <RevealItem>
                <div className="flex flex-col gap-6">
                  {highlights.map((item) => (
                    <div
                      key={item.id || item.title}
                      className="flex gap-5 border-l-4 border-primary-400 pl-5"
                    >
                      <div className="flex flex-col gap-1">
                        <h4 className="text-xl font-bold text-neutral-900">
                          {item.title}
                        </h4>
                        <p className="text-base text-neutral-500 leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </RevealItem>
            )}

            <RevealItem>
              <Link
                to="/subscribe"
                search={course?.slug ? { course: course.slug } : {}}
              >
                <Button size="lg" className="gap-2">
                  <LuSend />
                  Join This Course
                </Button>
              </Link>
            </RevealItem>
          </ScrollReveal>
        </div>
      </div>
    </section>
  )
}
